import { useState, useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const MembershipSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const planId = searchParams.get("plan_id");
  const [status, setStatus] = useState("checking");
  const [paymentData, setPaymentData] = useState(null);
  const hasPolled = useRef(false);

  useEffect(() => {
    if (hasPolled.current) return;
    hasPolled.current = true;

    // If we have a plan_id from Square redirect, show success immediately
    // (Square hosted checkout only redirects on successful payment)
    if (planId) {
      const planNames = { monthly: "Monthly", quarterly: "Quarterly", annual: "Annual" };
      setPaymentData({ metadata: { plan_name: planNames[planId] || planId } });
      setStatus("success");
      return;
    }

    if (!sessionId) {
      setStatus("success");
      return;
    }

    const pollPaymentStatus = async (sid, attempts) => {
      const maxAttempts = 6;
      const pollInterval = 2000;

      if (attempts >= maxAttempts) {
        setStatus("timeout");
        return;
      }

      try {
        const response = await axios.get(`${API}/membership/checkout/status/${sid}`);
        const data = response.data;

        if (data.payment_status === "paid") {
          setStatus("success");
          setPaymentData(data);
          return;
        } else if (data.status === "expired") {
          setStatus("expired");
          return;
        }

        setStatus("processing");
        setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
      } catch (error) {
        if (attempts < maxAttempts - 1) {
          setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
        } else {
          setStatus("error");
        }
      }
    };

    pollPaymentStatus(sessionId, 0);
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        {status === "checking" || status === "processing" ? (
          <div data-testid="payment-processing">
            <Loader2 className="w-16 h-16 text-primary mx-auto mb-6 animate-spin" />
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Processing Payment...</h1>
            <p className="text-slate-600">Please wait while we confirm your membership.</p>
          </div>
        ) : status === "success" ? (
          <div data-testid="payment-success">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-emerald-600" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Welcome to iConcierge!</h1>
            <p className="text-slate-600 mb-2">Your membership is now active.</p>
            {paymentData?.metadata?.plan_name && (
              <p className="text-primary font-semibold mb-6">
                {paymentData.metadata.plan_name} Plan
                {paymentData.amount_total && (
                  <span className="text-slate-500 font-normal"> - ${(paymentData.amount_total / 100).toFixed(2)}</span>
                )}
              </p>
            )}
            <div className="flex flex-col gap-3">
              <Button asChild className="bg-primary text-white hover:bg-primary/90 rounded-full">
                <Link to="/dashboard">
                  Go to Dashboard
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
              <Button asChild variant="outline" className="rounded-full">
                <Link to="/browse">Browse Providers</Link>
              </Button>
            </div>
          </div>
        ) : (
          <div data-testid="payment-issue">
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-amber-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              {status === "timeout" ? "Taking Longer Than Expected" : "Payment Issue"}
            </h1>
            <p className="text-slate-600 mb-6">
              {status === "timeout"
                ? "Your payment may still be processing. Check your email for confirmation."
                : "There was an issue verifying your payment. Please contact support."}
            </p>
            <Button asChild variant="outline" className="rounded-full">
              <Link to="/">Return Home</Link>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MembershipSuccess;
